/**
 * The Grid's facts as the page needs them: the same truths as the server's
 * `world.ts`, restated here because nothing under `server/` is reachable from
 * the bundle. Pure functions only.
 *
 * The page names all six Cell values, where the server names only the Wall
 * and the snare, the two its Walker's choices turn on: the page has a Brush per
 * value and a glyph per value, and those two have to agree.
 */

export const GRID_SIDE = 24
export const GRID_CELLS = GRID_SIDE * GRID_SIDE

/** The six values a Cell holds, on the wire and in the Grid string. */
export const EMPTY = 0
export const KICK = 1
export const SNARE = 2
export const HAT = 3
export const TOM = 4
export const WALL = 5

/** The sound a Cell holds, as the Grid numbers it: 0 is a rest, and a Wall is one too. */
export type EventKind = typeof EMPTY | typeof KICK | typeof SNARE | typeof HAT | typeof TOM

/** A Bar is 16 Steps, and the server counts a Step's place in it, 0 to 15. */
export const STEPS_PER_BAR = 16

/** Beats 2 and 4, the counts a snare is meant to land on. */
export const SNARE_COUNTS: ReadonlyArray<number> = [4, 12]

export const isCount = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value < STEPS_PER_BAR

/** An Action: the way the Walker steps from a Cell to one of its neighbours. */
export type Action = "up" | "down" | "left" | "right"

/** The four Actions from a Cell and the Cells they land on, wrapping: there are no edges. */
export const neighbours = (cell: number): ReadonlyArray<readonly [Action, number]> => {
  const row = Math.floor(cell / GRID_SIDE)
  const col = cell % GRID_SIDE
  const at = (r: number, c: number) =>
    ((r + GRID_SIDE) % GRID_SIDE) * GRID_SIDE + ((c + GRID_SIDE) % GRID_SIDE)
  return [
    ["up", at(row - 1, col)],
    ["down", at(row + 1, col)],
    ["left", at(row, col - 1)],
    ["right", at(row, col + 1)]
  ]
}

/** A batch of Cell writes, the shape a paint travels and is applied in. */
export type PaintCells = ReadonlyArray<readonly [number, number]>

export const isCellIndex = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value < GRID_CELLS

export const isCellValue = (value: number): boolean =>
  Number.isInteger(value) && value >= EMPTY && value <= WALL

/** The value at a Cell. Out of range reads as a Wall, as it does on the server. */
export const cellValue = (grid: string, cell: number): number => {
  const digit = grid[cell]
  return digit === undefined ? WALL : Number(digit)
}

/** One Cell written into the Grid string. An out-of-range index leaves it untouched. */
export const paintCell = (grid: string, cell: number, value: number): string =>
  isCellIndex(cell) && isCellValue(value) ? grid.slice(0, cell) + String(value) + grid.slice(cell + 1) : grid

/** A whole paint written into the Grid string, in order, the last write winning. */
export const paintCells = (grid: string, cells: PaintCells): string =>
  cells.reduce((written, [cell, value]) => paintCell(written, cell, value), grid)

/** The Event a Cell sounds when the Walker lands on it. An empty Cell and a Wall both sound silence. */
export const eventAt = (grid: string, cell: number): EventKind => {
  const value = cellValue(grid, cell)
  return value >= KICK && value <= TOM ? (value as EventKind) : 0
}

/**
 * The Cells that come to sound differently between two Grids, in Grid order.
 * A Cell that changed without changing its Event is not among them: a Wall and
 * an empty Cell both sound silence, so walking onto either is the same rest.
 */
export const changedEvents = (before: string, after: string): ReadonlyArray<number> => {
  if (before === after) return []
  const changed: Array<number> = []
  for (let cell = 0; cell < GRID_CELLS; cell++) {
    if (before[cell] !== after[cell] && eventAt(before, cell) !== eventAt(after, cell)) changed.push(cell)
  }
  return changed
}
